import React, { useState, useEffect, useCallback } from 'react';
import { 
  HelpCircle, ChevronRight, ChevronDown, Search, Book, Calendar, 
  DollarSign, Sparkles, AlertTriangle, Loader2, ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import ReactMarkdown from 'react-markdown';

const SECTION_ICONS = {
  'faq': HelpCircle,
  'post-scheduling': Calendar,
  'bank-payouts': DollarSign,
  'upgrade': Sparkles,
  'troubleshooting': AlertTriangle
};

const HelpCenter = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const { token } = useAuth();
  const { isDark } = useTheme();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Detect device type
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch help sections
  const fetchHelpSections = useCallback(async () => {
    try {
      const deviceType = isMobile ? 'mobile' : 'desktop';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API_URL}/api/help?device_type=${deviceType}`,
        { headers }
      );
      
      if (!response.ok) throw new Error('Failed to fetch help');
      
      const data = await response.json();
      setSections(data.sections || []);
    } catch (error) {
      console.error('Failed to fetch help sections:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, isMobile]);

  useEffect(() => {
    fetchHelpSections();
  }, [fetchHelpSections]);

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Filter sections based on search
  const filteredSections = sections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      section.title.toLowerCase().includes(query) ||
      section.content.toLowerCase().includes(query)
    );
  });

  const getIcon = (slug) => {
    const IconComponent = SECTION_ICONS[slug] || Book;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <HelpCircle className="w-5 h-5 text-yellow-400" />
            Help Center
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {isMobile ? 'Quick answers to common questions' : 'Find answers, learn about features, and get support'}
          </p>
        </div>
        <Badge variant="outline" className="text-yellow-400 border-yellow-400/50 hidden sm:flex">
          {sections.length} topics
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <Input
          type="text"
          placeholder="Search help topics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
        />
      </div>

      {/* Help Sections */}
      {filteredSections.length === 0 ? (
        <Card className={isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}>
          <CardContent className="py-12 text-center">
            <HelpCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
              {searchQuery ? 'No results found' : 'No help content available'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSections.map((section) => {
            const Icon = getIcon(section.slug);
            const isExpanded = expandedSection === section.id;
            
            return (
              <Card 
                key={section.id}
                className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} overflow-hidden transition-all`}
              >
                {/* Section Header - Clickable */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full p-4 flex items-center justify-between text-left transition-colors min-h-[56px] ${
                    isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                  }`}
                  aria-expanded={isExpanded}
                  aria-controls={`help-content-${section.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                      <Icon className="w-5 h-5 text-yellow-500" aria-hidden="true" />
                    </div>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {section.title}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  ) : (
                    <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  )}
                </button>

                {/* Section Content - Expandable */}
                {isExpanded && (
                  <div 
                    id={`help-content-${section.id}`}
                    className={`px-4 pb-4 ${isDark ? 'border-t border-gray-700' : 'border-t border-gray-200'}`}
                  >
                    <div className={`prose prose-sm max-w-none mt-4 ${
                      isDark 
                        ? 'prose-invert prose-headings:text-white prose-p:text-gray-300 prose-strong:text-yellow-400 prose-li:text-gray-300' 
                        : 'prose-headings:text-gray-900 prose-p:text-gray-600 prose-strong:text-yellow-600'
                    }`}>
                      <ReactMarkdown
                        components={{
                          // Style headings
                          h2: ({node, ...props}) => (
                            <h2 className={`text-lg font-bold mt-4 mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />
                          ),
                          h3: ({node, ...props}) => (
                            <h3 className={`text-base font-semibold mt-3 mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`} {...props} />
                          ),
                          // Style paragraphs
                          p: ({node, ...props}) => (
                            <p className={`my-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} {...props} />
                          ),
                          // Style lists
                          ul: ({node, ...props}) => (
                            <ul className={`list-disc list-inside my-2 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} {...props} />
                          ),
                          ol: ({node, ...props}) => (
                            <ol className={`list-decimal list-inside my-2 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} {...props} />
                          ),
                          // Style bold/strong - highlight buttons
                          strong: ({node, children, ...props}) => {
                            const text = String(children);
                            // Check if it's a button reference
                            const isButton = ['Publish Now', 'Schedule Post', 'Link Bank', 'Relink Bank', 
                                            'Request Payout', 'Upgrade to Own Ya Royalties', 'Own Ya Royalties',
                                            '$30.00 per year', 'minimum payout is $50', 'Minimum payout is $50'].some(
                              btn => text.includes(btn)
                            );
                            return (
                              <strong 
                                className={isButton 
                                  ? 'text-yellow-400 font-semibold' 
                                  : isDark ? 'text-white font-semibold' : 'text-gray-900 font-semibold'
                                } 
                                {...props}
                              >
                                {children}
                              </strong>
                            );
                          },
                          // Style code/inline code
                          code: ({node, ...props}) => (
                            <code className={`px-1.5 py-0.5 rounded text-sm ${isDark ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-yellow-600'}`} {...props} />
                          )
                        }}
                      >
                        {section.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Contact Support Card */}
      <Card className={`${isDark ? 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                <ExternalLink className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Still need help?
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Contact our support team
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-yellow-400 text-yellow-400 hover:bg-yellow-400/10 min-h-[44px]"
              onClick={() => window.location.href = 'mailto:support@igotsoul.com'}
            >
              Contact
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
